import {useState, useContext } from "react";
import DataTable from "core/ui/data-table";
import {Link, withRouter} from 'react-router-dom';
import Map, {emptyGeoJSON, MapLocator} from "core/ui/mapping/map";
import mapboxgl from 'mapbox-gl';
import { Buffer } from 'buffer';

import recolor_svg from "../core/utils/recolor_svg";
import SessionContext from 'SessionContext'
import { Button, Collapse } from "reactstrap";

const table_columns = [
  { header: 'Type', key: 'location_type__name'},
  { header: 'Name', key: 'name'},
  { header: 'Address', key: 'address', render: (loc)=> [loc.address1, loc.address2, (loc.city +', '+ loc.state), loc.zip4 && loc.zip4.length > 0 ? (loc.zip5 + '-' + loc.zip4) : loc.zip5 ].join(' ')},
];

const LocationActions = (props) => {
  return (
    <ul className="action-menu">
      <MapLocator map={props.map} flyTo={{ center: [props.lng, props.lat] }}>Fly To</MapLocator>
      {props.access('location_edit') &&
        <Link className="edit-loc" to={'/location-form/#location_id=' + props.id}><i className="bi bi-pencil"></i>Edit Location</Link>
      }
      </ul>
  );
}

export function extractIcon(svg){
  const frag = document.createRange().createContextualFragment(svg);
  const icon = frag.querySelector('svg');

  return icon;
}

const Locations = (props) => {
  const context = useContext(SessionContext);
  const [locations, setLocations] = useState([]);
  const [map, setMap] = useState();
  const [mapIsOpen, setMapIsOpen] = useState(true);

  function onMapLoaded(map){
    const popup = new mapboxgl.Popup({
      closeButton: false,
      closeOnClick: false,
      anchor: 'top'
    });

    if (!map.getSource('location-points')) {
      map.addSource('location-points', emptyGeoJSON({generateId: true}));
    }
    map.addLayer({
        "id": "location-points",
        "source": "location-points",
        "type": "symbol",
        'layout': {
          'icon-image': 'map-marker-{location_type__name}',
          'icon-anchor': 'bottom',
          'icon-allow-overlap': true,
        },
        'paint': {
          'icon-opacity': 1
        }
      });
    bindLocationPopupToLayer(map, 'location-points', popup);
    map.addLayer({
      "id": "location-points-hover",
      "source": "location-points",
      "type": "symbol",
      'layout': {
        'icon-image': 'map-marker-{location_type__name}',
        'icon-anchor': 'bottom',
        'icon-allow-overlap': true,
        'icon-size': 1.2,
        'visibility': 'none',
      },
      'paint': {
        'icon-opacity': ['case', ['boolean', ['feature-state', 'hover'], false], 1, 0]
      }
    });
    map.addLayer({
      "id": "location-points-no-hover",
      "source": "location-points",
      "type": "symbol",
      'layout': {
        'icon-image': 'map-marker-{location_type__name}',
        'icon-anchor': 'bottom',
        'icon-allow-overlap': true,
        'icon-size': 1,
        'visibility': 'none',
      },
      'paint': {
        'icon-opacity': ['case', ['boolean', ['feature-state', 'hover'], false], 0, .2]
      }
    });
    context.query('location/location-types', {companyId: 0}).then((location_types)=>{
      for(const location_type of location_types){
        const buff = Buffer.from(recolor_svg(location_type.svg, location_type.color, "currentColor"));
        const base64data = buff.toString('base64');
        const src=`data:image/svg+xml;base64,${base64data}`;
        map.addNamedImage(`map-marker-${location_type.name}-${location_type.id}`, src, 25, 25);
      }
      setMap(map);
      tryUpdateLocationsOnMap(map, locations);
    });
    context.query('location/location-types', {companyId: context.appState.activeCompany.id}).then((location_types)=>{
      for(const location_type of location_types){
        const buff = Buffer.from(recolor_svg(location_type.svg, location_type.color, "currentColor"));
        const base64data = buff.toString('base64');

        const src=`data:image/svg+xml;base64,${base64data}`;
        map.addNamedImage(`map-marker-${location_type.name}`, src, 25, 25);
      }
      setMap(map);
      tryUpdateLocationsOnMap(map, locations);
    });
  }

  function bindLocationPopupToLayer(map, layer, popup){
    map.on('mouseenter', layer, function(e) {
      var coordinates = e.features[0].geometry.coordinates.slice();

      // Populate the popup and set its coordinates
      // based on the feature found.
      popup.setLngLat(coordinates)
          .setHTML(e.features[0].properties.name)
          .addTo(map);
    });

    map.on('mouseleave', layer, function() {
      popup.remove();
    });
  }

  function onMapUnloaded(_map){
    setMap(undefined);
  }

  function onLocationsUpdate(locations){
    setLocations(locations);
    tryUpdateLocationsOnMap(map, locations);
  }

  function tryUpdateLocationsOnMap(currentMap, currentLocations){
    if(!currentLocations || !currentMap) return;

    currentMap.getSource('location-points').setData({
      "type": "FeatureCollection",
      "features": currentLocations.map((location) => ({
        "type": "Feature",
        'properties': location,
        "geometry": {
          "type": "Point",
          "coordinates": [location.lng, location.lat]
        }
      }))
    });
    if(currentLocations.length > 0) {
      const bounds = new mapboxgl.LngLatBounds();
      currentLocations.forEach((loc) => bounds.extend([loc.lng, loc.lat]));
      currentMap.fitBounds(bounds, {padding: 100, duration: 1000});
    }
  }

  function setLocationHoverState(value, loc){
    if(!map) return;
    const feature_id = locations.findIndex(l=> l.id === loc.id);
    if(feature_id > -1) {
      map.setFeatureState({source: 'location-points', id: feature_id}, {hover: value});
    }
    map.setPaintProperty('location-points', 'icon-opacity', !value ? 1:0);
    map.setLayoutProperty('location-points-hover', 'visibility', value ? 'visible':'none');
    map.setLayoutProperty('location-points-no-hover', 'visibility', value ? 'visible':'none');
  }

  function flyToLocation(loc){
    if(!map) return;
    map.flyTo({center: [loc.lng, loc.lat], zoom: 9});
  }

  // eslint-disable-next-line no-unused-vars
  function onClickNew(ev) {
    ev.stopPropagation();
    props.history.push('/location-form')
  }

  function onClickUploads(ev) {
    props.history.push('/location-uploads');
  }

  function toggleMapOpen() {
    setMapIsOpen(prev => !prev);
    setTimeout(() => { window.dispatchEvent(new Event('resize')) }, 500);
  }

  // User permission callback. 
  const access = context.appState.userCan;
  return (
    <div className='locations-page-wrapper'>
      <Collapse isOpen={mapIsOpen}>
        <Map onMount={onMapLoaded}
              onUnmount={onMapUnloaded}
              mapstate={{
              center: context.crossfilterStore.map_center,
              zoom: context.crossfilterStore.map_zoom
          }}/>
      </Collapse>

        <div className="page locations-page">
          <div className="list-header sticky-page-header">
          <h1>Locations</h1>
          <Button color="light" className='new-button map-expand' onClick={toggleMapOpen}>
            {(mapIsOpen) ? "Hide Map " : "Open Map "}
            <i className={(mapIsOpen) ? "bi bi-chevron-up" : "bi bi-chevron-down"}></i>
          </Button>
          {access('location_create') &&
            <Button className='new-button' onClick={onClickUploads}><i className="fa fa-location-dot"></i> Location Uploads</Button>
          }
            {/* HIDDING UNTIL LONG/LAT HAS BEEN COMPLETED IN BACK END -- PLEASE REMOVE THIS WHEN UN-HIDDING
              <i className="spacer" /><button className='new-button' onClick={onClickNew}>New Location</button> 
              */}
          </div>
          <div className='locations-list'>
            <DataTable query='location/list'
                        cols={table_columns}
                        actions={{component: LocationActions, 
                          map: map,
                          access: access
                        }}
                        empty='No Locations Found'
                        onUpdate={onLocationsUpdate}
                        row_onMouseEnter={(loc) => setLocationHoverState(true, loc)}
                        row_onMouseLeave={(loc) => setLocationHoverState(false, loc)}
                        row_onClick={flyToLocation}
          />
          </div>
        </div>
      </div>
  );
};
export default withRouter(Locations);
